// Copyright (c) 2025 UXtechLab.
// Originally created by @taekie. Released under the MIT License. See LICENSE for details.
/**
 * LabelAdjuster
 *
 * Handles automatic label collision detection and position adjustment
 * for voronoi treemap visualizations. Adjusts label positions to prevent
 * overlapping between:
 * - Field labels and region labels
 * - Sector labels and field labels
 *
 * Uses setTimeout-based deferred processing to ensure DOM elements
 * are fully rendered before measuring and adjusting positions.
 */

import d3 from './utils/d3-bundle.js';

/**
 * LabelAdjuster - Label collision detection and adjustment
 *
 * This class provides methods to automatically adjust label positions
 * in voronoi treemaps to prevent overlapping text elements.
 */
export class LabelAdjuster {
  /**
   * Create a LabelAdjuster instance
   * @param {Object} [d3Instance] - Optional D3 instance (defaults to global d3)
   */
  constructor(d3Instance) {
    this.d3 = d3Instance || d3;
  }

  /**
   * Adjust label positions in a treemap SVG to prevent overlapping
   * @param {SVGElement} treemap - The SVG element containing the treemap
   * @param {Object} [options={}] - Adjustment options
   * @param {number} [options.verticalSpacing=0] - Additional vertical spacing between labels
   * @param {number} [options.delay=100] - Delay in ms before adjustment (for DOM rendering)
   * @param {number} [options.maxParentMove=18] - Maximum group-label movement in pixels
   * @param {number} [options.cellPadding=2] - Minimum label padding from polygon edges
   */
  adjust(treemap, options = {}) {
    const {
      verticalSpacing = 0,
      delay = 100,
      maxParentMove = 18,
      cellPadding = 2
    } = options;
    const d3 = this.d3;

    setTimeout(() => {
      const svg = d3.select(treemap);

      svg.selectAll(".vb-subgroup-label, .vb-subgroup-label-html").each(function () {
        adjustFieldLabel(d3.select(this));
      });

      svg.selectAll(".vb-item-label").each(function () {
        adjustSectorLabel(d3.select(this));
      });
    }, delay);

    /**
     * Parse SVG transform attribute to extract x, y translation
     * @param {string} transform - Transform attribute string
     * @returns {Object} { x, y } translation values
     */
    function parseTransform(transform) {
      if (!transform) return { x: 0, y: 0 };
      const match = transform.match(/translate\(([^,]+),([^)]+)\)/);
      if (!match) return { x: 0, y: 0 };
      return { x: parseFloat(match[1]), y: parseFloat(match[2]) };
    }

    /**
     * Check if two bounding boxes overlap
     * @param {Object} box1 - First bounding box
     * @param {Object} box2 - Second bounding box
     * @returns {boolean} True if boxes overlap
     */
    function checkOverlap(box1, box2) {
      const margin = verticalSpacing / 2;
      const marginx = -3;
      return !(
        box1.x + box1.width / 2 + marginx < box2.x - box2.width / 2 ||
        box1.x - box1.width / 2 - marginx > box2.x + box2.width / 2 ||
        box1.y + box1.height / 2 + margin < box2.y - box2.height / 2 ||
        box1.y - box1.height / 2 - margin > box2.y + box2.height / 2
      );
    }

    /**
     * Calculate required vertical move distance to resolve overlap
     * @param {Object} box1 - First bounding box
     * @param {Object} box2 - Second bounding box
     * @returns {number} Required move distance
     */
    function getRequiredMoveDistance(box1, box2) {
      const margin = verticalSpacing / 2;
      const box1Top = box1.y - box1.height / 2 - margin;
      const box1Bottom = box1.y + box1.height / 2 + margin;
      const box2Top = box2.y - box2.height / 2 - margin;
      const box2Bottom = box2.y + box2.height / 2 + margin;

      if (box1Bottom <= box2Top || box1Top >= box2Bottom) return 0;
      if (box1.y < box2.y) return box1Bottom - box2Top;
      return box2Bottom - box1Top;
    }

    /**
     * Get bounding box information for a label element
     * @param {Object} element - D3 selection of label element
     * @returns {Object|null} Bounding box info or null if element invalid
     */
    function getLabelBox(element) {
      const node = element.node();
      if (!node) return null;

      const bbox = node.getBBox();
      const { width, height } = bbox;
      const tspanCount = element.selectAll("tspan tspan").size() || 1;
      const transform = parseTransform(element.attr("transform"));

      return {
        originalX: transform.x,
        originalY: transform.y,
        x: transform.x + bbox.x + width / 2,
        y: transform.y + bbox.y + height / 2,
        width,
        height,
        tspanCount
      };
    }

    /**
     * Get bounding box from a foreignObject element (renderGroupLabel case)
     * foreignObject uses x/y/width/height attributes instead of transform+getBBox
     * @param {Object} element - D3 selection of foreignObject element
     * @returns {Object|null} Bounding box info or null if element invalid
     */
    function getForeignObjectBox(element) {
      const node = element.node();
      if (!node) return null;

      const foX = parseFloat(element.attr("x") || 0);
      const foY = parseFloat(element.attr("y") || 0);
      const foW = parseFloat(element.attr("width") || 0);
      const foH = parseFloat(element.attr("height") || 0);
      if (foW === 0 || foH === 0) return null;

      // Use actual rendered content size — querySelector('div') is the flex wrapper (100%x100%),
      // so use its firstElementChild which is the actual rendered content from renderGroupLabel
      let width = foW;
      let height = foH;
      const flexWrapper = node.querySelector("div");
      const contentEl = flexWrapper ? flexWrapper.firstElementChild : null;
      const target = contentEl || flexWrapper;
      if (target) {
        const rect = target.getBoundingClientRect();
        const matrix = node.getScreenCTM();
        if (rect.width > 0 && rect.height > 0 && matrix) {
          const svg = node.ownerSVGElement;
          const topLeft = svg.createSVGPoint();
          const bottomRight = svg.createSVGPoint();
          topLeft.x = rect.left;
          topLeft.y = rect.top;
          bottomRight.x = rect.right;
          bottomRight.y = rect.bottom;
          const inverse = matrix.inverse();
          const localTopLeft = topLeft.matrixTransform(inverse);
          const localBottomRight = bottomRight.matrixTransform(inverse);
          width = Math.abs(localBottomRight.x - localTopLeft.x);
          height = Math.abs(localBottomRight.y - localTopLeft.y);
        }
      }

      // Content is flex-centered within the foreignObject
      const centerX = foX + foW / 2;
      const centerY = foY + foH / 2;
      const contentTop = centerY - height / 2;

      return {
        originalX: centerX,
        originalY: contentTop,
        x: centerX,
        y: centerY,
        width,
        height,
        tspanCount: 1
      };
    }

    /**
     * Get cell polygon bounds from node data
     * @param {Object} data - Node data with polygon
     * @returns {Object|null} { minX, maxX, minY, maxY } or null
     */
    function getCellBounds(data) {
      if (!data || !data.polygon) return null;
      const xs = data.polygon.map((p) => p[0]);
      const ys = data.polygon.map((p) => p[1]);
      return {
        minX: Math.min(...xs),
        maxX: Math.max(...xs),
        minY: Math.min(...ys),
        maxY: Math.max(...ys)
      };
    }

    function moveBox(box, dx, dy) {
      return {
        ...box,
        originalX: box.originalX + dx,
        originalY: box.originalY + dy,
        x: box.x + dx,
        y: box.y + dy
      };
    }

    function boxFitsPolygon(box, polygon) {
      if (!polygon?.length) return false;
      const halfWidth = box.width / 2 + cellPadding;
      const halfHeight = box.height / 2 + cellPadding;
      const points = [
        [box.x - halfWidth, box.y - halfHeight],
        [box.x, box.y - halfHeight],
        [box.x + halfWidth, box.y - halfHeight],
        [box.x + halfWidth, box.y],
        [box.x + halfWidth, box.y + halfHeight],
        [box.x, box.y + halfHeight],
        [box.x - halfWidth, box.y + halfHeight],
        [box.x - halfWidth, box.y]
      ];
      return points.every((point) => d3.polygonContains(polygon, point));
    }

    function setLabelPosition(label, box, x, y) {
      if (label.node()?.tagName === "foreignObject") {
        const dx = x - box.originalX;
        const dy = y - box.originalY;
        label
          .attr("x", parseFloat(label.attr("x") || 0) + dx)
          .attr("y", parseFloat(label.attr("y") || 0) + dy);
      } else {
        label.attr("transform", `translate(${x},${y})`);
      }
    }

    function movementCandidates(labelBox, blockerBox) {
      const gap = 2 + verticalSpacing / 2;
      const left = blockerBox.x - blockerBox.width / 2 - labelBox.width / 2 - gap;
      const right = blockerBox.x + blockerBox.width / 2 + labelBox.width / 2 + gap;
      const above = blockerBox.y - blockerBox.height / 2 - labelBox.height / 2 - gap;
      const below = blockerBox.y + blockerBox.height / 2 + labelBox.height / 2 + gap;

      return [
        { dx: 0, dy: above - labelBox.y },
        { dx: 0, dy: below - labelBox.y },
        { dx: left - labelBox.x, dy: 0 },
        { dx: right - labelBox.x, dy: 0 },
        { dx: left - labelBox.x, dy: above - labelBox.y },
        { dx: right - labelBox.x, dy: above - labelBox.y },
        { dx: left - labelBox.x, dy: below - labelBox.y },
        { dx: right - labelBox.x, dy: below - labelBox.y }
      ].sort((a, b) => Math.hypot(a.dx, a.dy) - Math.hypot(b.dx, b.dy));
    }

    function findPolygonMove(labelBox, blockerBox, polygon) {
      if (!checkOverlap(labelBox, blockerBox)) return { dx: 0, dy: 0 };
      return movementCandidates(labelBox, blockerBox).find(({ dx, dy }) => {
        const moved = moveBox(labelBox, dx, dy);
        return boxFitsPolygon(moved, polygon) && !checkOverlap(moved, blockerBox);
      }) || null;
    }

    function parentMovementCandidates(parentBox, polygon) {
      const candidates = [{ dx: 0, dy: 0 }];
      const step = 3;
      for (let radius = step; radius <= maxParentMove; radius += step) {
        for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
          const dx = Math.cos(angle) * radius;
          const dy = Math.sin(angle) * radius;
          if (boxFitsPolygon(moveBox(parentBox, dx, dy), polygon)) {
            candidates.push({ dx, dy });
          }
        }
      }
      return candidates;
    }

    /**
     * Calculate minimum move position to avoid overlap
     * @param {Object} labelBox - Label bounding box
     * @param {Object} parentBox - Parent label bounding box
     * @param {Object} cellBounds - Cell polygon bounds
     * @returns {Object} { x, y } new position
     */
    function findMinimumMove(labelBox, parentBox, cellBounds) {
      if (
        labelBox.x + labelBox.width / 2 < parentBox.x - parentBox.width / 2 ||
        labelBox.x - labelBox.width / 2 > parentBox.x + parentBox.width / 2
      ) {
        return { x: labelBox.originalX, y: labelBox.originalY };
      }

      const moveDistance = getRequiredMoveDistance(labelBox, parentBox);
      if (moveDistance === 0) {
        return { x: labelBox.originalX, y: labelBox.originalY };
      }

      const tspanOffset =
        labelBox.tspanCount > 1
          ? labelBox.height / (labelBox.tspanCount - 1) / 4
          : 0;

      // Prefer the direction with more available space
      const spaceAbove = parentBox.originalY - cellBounds.minY;
      const spaceBelow = cellBounds.maxY - (parentBox.originalY + parentBox.height);
      const preferBelow = spaceBelow > spaceAbove;

      // Primary direction: whichever side has more room (+2px gap)
      const proposedPrimary = preferBelow
        ? parentBox.originalY + parentBox.height + 2 + tspanOffset
        : parentBox.originalY - labelBox.height - 2 + tspanOffset;

      const primaryFits = preferBelow
        ? proposedPrimary + labelBox.height <= cellBounds.maxY
        : proposedPrimary >= cellBounds.minY;

      if (primaryFits) {
        return { x: labelBox.originalX, y: proposedPrimary };
      }

      // Secondary direction: the other side (+2px gap)
      const proposedSecondary = preferBelow
        ? parentBox.originalY - labelBox.height - 2 + tspanOffset
        : parentBox.originalY + parentBox.height + 2 + tspanOffset;

      const secondaryFits = preferBelow
        ? proposedSecondary >= cellBounds.minY
        : proposedSecondary + labelBox.height <= cellBounds.maxY;

      if (secondaryFits) {
        return { x: labelBox.originalX, y: proposedSecondary };
      }

      // Neither direction fits within cell — leave in original position
      return { x: labelBox.originalX, y: labelBox.originalY };
    }

    /**
     * Adjust sector label position if overlapping with parent field label
     * @param {Object} sectorLabel - D3 selection of sector label
     */
    function adjustSectorLabel(sectorLabel) {
      const data = sectorLabel.datum();
      if (!data || !data.parent) return;

      const parentFieldElement = d3
        .select(treemap)
        .selectAll(".vb-subgroup-label")
        .filter((d) => d?.data?.key === data.parent?.data?.key)
        .nodes()[0];

      if (!parentFieldElement) return;

      const fieldLabel = d3.select(parentFieldElement);
      const sectorBox = getLabelBox(sectorLabel);
      const fieldBox = getLabelBox(fieldLabel);
      const cellBounds = getCellBounds(data);

      if (
        !cellBounds ||
        !sectorBox ||
        !fieldBox ||
        sectorBox.width === 0 ||
        sectorBox.height === 0 ||
        fieldBox.width === 0 ||
        fieldBox.height === 0
      ) {
        return;
      }

      if (checkOverlap(sectorBox, fieldBox)) {
        const move = findPolygonMove(sectorBox, fieldBox, data.polygon);
        const newPos = move
          ? { x: sectorBox.originalX + move.dx, y: sectorBox.originalY + move.dy }
          : findMinimumMove(sectorBox, fieldBox, cellBounds);
        setLabelPosition(sectorLabel, sectorBox, newPos.x, newPos.y);
      }
    }

    /**
     * Adjust field label position if overlapping with parent region label
     * @param {Object} fieldLabel - D3 selection of field label
     */
    function adjustFieldLabel(fieldLabel) {
      const data = fieldLabel.datum();
      if (!data || !data.parent) return;

      const parentKey = data.parent?.data?.key;

      // Default renderer: SVG text.vb-group-label
      let parentRegionElement = d3
        .select(treemap)
        .selectAll(".vb-group-label")
        .filter((d) => d?.data?.key === parentKey)
        .nodes()[0];

      // Custom renderer (renderGroupLabel): foreignObject.vb-group-label-html
      if (!parentRegionElement) {
        parentRegionElement = d3
          .select(treemap)
          .selectAll(".vb-group-label-html")
          .filter((d) => d?.data?.key === parentKey)
          .nodes()[0];
      }

      if (!parentRegionElement) return;

      const regionLabel = d3.select(parentRegionElement);
      const isFieldForeignObject = fieldLabel.node()?.tagName === "foreignObject";
      const fieldBox = isFieldForeignObject
        ? getForeignObjectBox(fieldLabel)
        : getLabelBox(fieldLabel);
      const isForeignObject = parentRegionElement.tagName === "foreignObject";
      const regionBox = isForeignObject
        ? getForeignObjectBox(regionLabel)
        : getLabelBox(regionLabel);
      const cellBounds = getCellBounds(data);
      const regionData = regionLabel.datum();
      const siblingBoxes = d3
        .select(treemap)
        .selectAll(".vb-subgroup-label, .vb-subgroup-label-html")
        .filter((d) => d !== data && d?.parent === data.parent)
        .nodes()
        .map((node) => {
          const selection = d3.select(node);
          return node.tagName === "foreignObject"
            ? getForeignObjectBox(selection)
            : getLabelBox(selection);
        })
        .filter(Boolean);

      if (
        !cellBounds ||
        !fieldBox ||
        !regionBox ||
        fieldBox.width === 0 ||
        fieldBox.height === 0 ||
        regionBox.width === 0 ||
        regionBox.height === 0
      ) {
        return;
      }

      const regionKey = regionLabel.datum()?.data?.key;
      if (
        regionKey &&
        String(regionKey).match(/[^ ]/) &&
        checkOverlap(fieldBox, regionBox)
      ) {
        let best = null;
        parentMovementCandidates(regionBox, regionData?.polygon).forEach((parentMove) => {
          const movedRegion = moveBox(regionBox, parentMove.dx, parentMove.dy);
          if (siblingBoxes.some((box) => checkOverlap(movedRegion, box))) return;
          const fieldMove = findPolygonMove(fieldBox, movedRegion, data.polygon);
          if (!fieldMove) return;

          // Moving a group label is visually more expensive than moving its child.
          const score = Math.hypot(fieldMove.dx, fieldMove.dy) +
            Math.hypot(parentMove.dx, parentMove.dy) * 2.5;
          if (!best || score < best.score) best = { parentMove, fieldMove, score };
        });

        if (best) {
          setLabelPosition(
            regionLabel,
            regionBox,
            regionBox.originalX + best.parentMove.dx,
            regionBox.originalY + best.parentMove.dy
          );
          setLabelPosition(
            fieldLabel,
            fieldBox,
            fieldBox.originalX + best.fieldMove.dx,
            fieldBox.originalY + best.fieldMove.dy
          );
        } else {
          const newPos = findMinimumMove(fieldBox, regionBox, cellBounds);
          setLabelPosition(fieldLabel, fieldBox, newPos.x, newPos.y);
        }
      }
    }
  }
}

export default LabelAdjuster;
